// Gamification System for Profound Philosophy
// Points, Levels, Badges, Achievements, and Streaks

import { Eye, Award, Target, Zap, TrendingUp, Trophy, Star, Crown, Flame, CheckCircle, Brain, Users, Sparkles, BookOpen, Calendar } from 'lucide-react';

// ============================================
// LEVELS & RANKS
// ============================================

// Thresholds are sized to the app's actual point economy (~3,500 earnable from
// completing everything plus modest streaks) so the top level is reachable in
// months of real use, not years of daily logins.
export const levels = [
  { level: 1, title: 'Curious Wonderer', minPoints: 0, maxPoints: 49, icon: Eye, color: 'slate' },
  { level: 2, title: 'Question Asker', minPoints: 50, maxPoints: 149, icon: BookOpen, color: 'blue' },
  { level: 3, title: 'Careful Reasoner', minPoints: 150, maxPoints: 299, icon: Target, color: 'cyan' },
  { level: 4, title: 'Assumption Hunter', minPoints: 300, maxPoints: 549, icon: Zap, color: 'teal' },
  { level: 5, title: 'Critical Thinker', minPoints: 550, maxPoints: 899, icon: Brain, color: 'green' },
  { level: 6, title: 'Ethical Navigator', minPoints: 900, maxPoints: 1349, icon: Award, color: 'amber' },
  { level: 7, title: 'Deep Examiner', minPoints: 1350, maxPoints: 1899, icon: Trophy, color: 'orange' },
  { level: 8, title: 'Wisdom Seeker', minPoints: 1900, maxPoints: 2549, icon: Star, color: 'red' },
  { level: 9, title: 'Socratic Guide', minPoints: 2550, maxPoints: 3299, icon: Sparkles, color: 'purple' },
  { level: 10, title: 'Lover of Wisdom', minPoints: 3300, maxPoints: Infinity, icon: Crown, color: 'pink' }
];

export const getLevelInfo = (points) => {
  const currentLevel = levels.find(l => points >= l.minPoints && points <= l.maxPoints) || levels[0];
  const nextLevel = levels.find(l => l.level === currentLevel.level + 1);

  const progressInLevel = points - currentLevel.minPoints;
  const pointsNeededForLevel = nextLevel ? nextLevel.minPoints - currentLevel.minPoints : 0;
  const percentageToNextLevel = nextLevel ? Math.round((progressInLevel / pointsNeededForLevel) * 100) : 100;

  return {
    currentLevel,
    nextLevel,
    progressInLevel,
    pointsNeededForLevel,
    percentageToNextLevel,
    pointsToNextLevel: nextLevel ? nextLevel.minPoints - points : 0
  };
};

// ============================================
// POINT AWARDS
// ============================================

export const pointValues = {
  // Assessment Activities
  COMPLETE_ASSESSMENT: 50,
  RETAKE_ASSESSMENT: 25,
  IMPROVE_DIMENSION: 10, // Per dimension improved
  PERFECT_DIMENSION: 20, // Score 100 on any dimension

  // Module Activities
  START_MODULE: 10,
  COMPLETE_MODULE: 100,
  COMPLETE_MODULE_CHALLENGE: 15,
  FINISH_ALL_MODULES: 200, // Bonus for completing all 8

  // Tool Activities
  FIRST_TOOL_USE: 15,
  TOOL_SESSION: 5,
  USE_ALL_TOOLS: 150, // Bonus for using all 18 tools
  TOOL_MILESTONE_10: 25, // Use any tool 10 times
  TOOL_MILESTONE_50: 50, // Use any tool 50 times

  // Weekly Challenges
  WEEKLY_CHALLENGE_BEGINNER: 10,
  WEEKLY_CHALLENGE_INTERMEDIATE: 20,
  WEEKLY_CHALLENGE_ADVANCED: 30,
  WEEKLY_CHALLENGES_5: 50, // Complete 5 weekly challenges
  WEEKLY_CHALLENGES_10: 100, // Complete 10 weekly challenges

  // Streak Bonuses
  DAILY_LOGIN: 5,
  STREAK_7_DAYS: 35,
  STREAK_30_DAYS: 150,
  STREAK_100_DAYS: 500,
  WEEKLY_CHALLENGE_STREAK_4: 80, // 4 weeks in a row

  // Special Activities
  PROFILE_COMPLETE: 15,
  FIRST_LOGIN: 10,
  SHARE_ACHIEVEMENT: 10 // Future: social sharing
};

// ============================================
// ACHIEVEMENTS / BADGES
// ============================================

export const achievements = {
  // Getting Started
  FIRST_STEPS: {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Complete your first module',
    icon: '👣',
    category: 'Getting Started',
    points: 0, // Already awarded in module completion
    checkUnlocked: (data) => Object.values(data.moduleProgress || {}).some(m => m.completed)
  },

  ASSESSMENT_COMPLETE: {
    id: 'assessment_complete',
    name: 'Know Thyself',
    description: 'Complete the Philosophical Profile assessment',
    icon: '🧠',
    category: 'Getting Started',
    points: 0,
    checkUnlocked: (data) => !!data.assessmentResults
  },

  TOOL_EXPLORER: {
    id: 'tool_explorer',
    name: 'Tool Explorer',
    description: 'Use your first interactive tool',
    icon: '🔧',
    category: 'Getting Started',
    points: 0,
    checkUnlocked: (data) => Object.keys(data.toolsUsage || {}).length > 0
  },

  // Module Achievements
  MODULE_MASTER: {
    id: 'module_master',
    name: 'Module Master',
    description: 'Complete all 8 philosophy modules',
    icon: '📚',
    category: 'Modules',
    points: 200,
    checkUnlocked: (data) => Object.values(data.moduleProgress || {}).filter(m => m.completed).length >= 8
  },

  EXAMINED_LIFE: {
    id: 'examined_life',
    name: 'The Examined Life',
    description: 'Complete Module 1: What is Philosophy?',
    icon: '🦉',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['what-is-philosophy']?.completed
  },

  CLEAR_THINKER: {
    id: 'clear_thinker',
    name: 'Clear Thinker',
    description: 'Complete Module 2: Logic & Reasoning',
    icon: '🧭',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['logic-and-reasoning']?.completed
  },

  KNOWLEDGE_SEEKER: {
    id: 'knowledge_seeker',
    name: 'Knowledge Seeker',
    description: 'Complete Module 3: Epistemology',
    icon: '🔍',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['epistemology']?.completed
  },

  MORAL_COMPASS: {
    id: 'moral_compass',
    name: 'Moral Compass',
    description: 'Complete Module 4: Ethics & Moral Philosophy',
    icon: '⚖️',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['ethics-moral-philosophy']?.completed
  },

  REALITY_EXPLORER: {
    id: 'reality_explorer',
    name: 'Reality Explorer',
    description: 'Complete Module 5: Metaphysics & Reality',
    icon: '🌌',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['metaphysics-reality']?.completed
  },

  CONSCIOUSNESS_INVESTIGATOR: {
    id: 'consciousness_investigator',
    name: 'Consciousness Investigator',
    description: 'Complete Module 6: Philosophy of Mind',
    icon: '🧠',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['philosophy-of-mind']?.completed
  },

  JUSTICE_ADVOCATE: {
    id: 'justice_advocate',
    name: 'Justice Advocate',
    description: 'Complete Module 7: Social & Political Philosophy',
    icon: '🏛️',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['social-political-philosophy']?.completed
  },

  MEANING_MAKER: {
    id: 'meaning_maker',
    name: 'Meaning Maker',
    description: 'Complete Module 8: Existentialism & Meaning',
    icon: '🗻',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['existentialism-meaning']?.completed
  },

  // Tool Achievements
  TOOL_MASTER: {
    id: 'tool_master',
    name: 'Tool Master',
    description: 'Use all 18 interactive tools',
    icon: '🛠️',
    category: 'Tools',
    points: 150,
    checkUnlocked: (data) => Object.keys(data.toolsUsage || {}).length >= 18
  },

  // Assessment Achievements
  GROWTH_MINDSET: {
    id: 'growth_mindset',
    name: 'Growth Mindset',
    description: 'Retake the assessment: growth means comparing who you were',
    icon: '📈',
    category: 'Growth',
    points: 25,
    checkUnlocked: (data) => (data.assessmentRetakes || 0) >= 1
  },

  // Challenge Achievements
  CHALLENGE_ACCEPTED: {
    id: 'challenge_accepted',
    name: 'Challenge Accepted',
    description: 'Complete your first weekly challenge',
    icon: '🎯',
    category: 'Challenges',
    points: 0,
    checkUnlocked: (data) => {
      const completed = Object.values(data.weeklyChallenges || {}).filter(status => status === 'completed');
      return completed.length >= 1;
    }
  },

  CHALLENGE_CRUSHER: {
    id: 'challenge_crusher',
    name: 'Challenge Crusher',
    description: 'Complete 10 weekly challenges',
    icon: '💪',
    category: 'Challenges',
    points: 100,
    checkUnlocked: (data) => {
      const completed = Object.values(data.weeklyChallenges || {}).filter(status => status === 'completed');
      return completed.length >= 10;
    }
  },

  CHALLENGE_LEGEND: {
    id: 'challenge_legend',
    name: 'Challenge Legend',
    description: 'Complete all 12 weekly challenges',
    icon: '🏅',
    category: 'Challenges',
    points: 250,
    checkUnlocked: (data) => {
      const completed = Object.values(data.weeklyChallenges || {}).filter(status => status === 'completed');
      return completed.length >= 12;
    }
  },

  // Streak Achievements
  WEEK_WARRIOR: {
    id: 'week_warrior',
    name: 'Week Warrior',
    description: 'Maintain a 7-day login streak',
    icon: '🔥',
    category: 'Streaks',
    points: 35,
    checkUnlocked: (data) => (data.streaks?.currentLoginStreak || 0) >= 7
  },

  MONTH_MASTER: {
    id: 'month_master',
    name: 'Month Master',
    description: 'Maintain a 30-day login streak',
    icon: '🌟',
    category: 'Streaks',
    points: 150,
    checkUnlocked: (data) => (data.streaks?.currentLoginStreak || 0) >= 30
  },

  CENTURION: {
    id: 'centurion',
    name: 'Centurion',
    description: 'Maintain a 100-day login streak',
    icon: '👑',
    category: 'Streaks',
    points: 500,
    checkUnlocked: (data) => (data.streaks?.currentLoginStreak || 0) >= 100
  },

};

// Helper function to get all unlocked achievements
export const getUnlockedAchievements = (userData) => {
  return Object.entries(achievements)
    .filter(([, achievement]) => achievement.checkUnlocked(userData))
    .map(([key, achievement]) => ({ ...achievement, key }));
};

// Helper function to get locked achievements (for display)
export const getLockedAchievements = (userData) => {
  return Object.entries(achievements)
    .filter(([, achievement]) => !achievement.checkUnlocked(userData))
    .map(([key, achievement]) => ({ ...achievement, key }));
};

// Helper function to check if new achievements were just unlocked
export const checkNewAchievements = (oldData, newData) => {
  const oldUnlocked = new Set(getUnlockedAchievements(oldData).map(a => a.key));
  const newUnlocked = getUnlockedAchievements(newData);

  return newUnlocked.filter(achievement => !oldUnlocked.has(achievement.key));
};

// ============================================
// STREAK TRACKING
// ============================================

export const updateStreaks = (currentStreaks, lastLoginDate) => {
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();

  const streaks = { ...currentStreaks };

  // Update login streak
  if (!lastLoginDate || lastLoginDate === today) {
    // Same day, no change
  } else if (lastLoginDate === yesterday) {
    // Consecutive day
    streaks.currentLoginStreak = (streaks.currentLoginStreak || 0) + 1;
    streaks.longestLoginStreak = Math.max(streaks.longestLoginStreak || 0, streaks.currentLoginStreak);
  } else {
    // Streak broken
    streaks.currentLoginStreak = 1;
  }

  return streaks;
};

export default {
  levels,
  getLevelInfo,
  pointValues,
  achievements,
  getUnlockedAchievements,
  getLockedAchievements,
  checkNewAchievements,
  updateStreaks
};
